import {
  addFootnoteByScopeSubscription,
  createFootnote,
  deleteFootnote,
  type GlobalFootnote,
  type GlobalFootnoteRecord,
} from '@dh-io-aem/footnote-manager';
import type { FootnoteGroup, FootnoteReferenceGroup } from '@dh-io-pls/price-display';
import { FOOTNOTE_CONTAINER_ID } from '../composables/footnotes.composable';

type ScopedState = Record<GlobalFootnoteRecord['content'], GlobalFootnoteRecord>;

const prepareInput = (
  footnoteGroups: FootnoteGroup[],
  reference: string,
): { key: GlobalFootnote['content']; payload: GlobalFootnote }[] =>
  footnoteGroups
    .map((group) => group.items)
    .flat()
    .map((text) => ({ key: text, payload: { type: 'global', content: text, reference } }));

const getFootnoteIds = (scopedState: ScopedState) =>
  Object.keys(scopedState).map((key) => {
    return scopedState[key].id;
  });

const produceScopedState = (
  scopedState: ScopedState,
  footnotes: GlobalFootnoteRecord[],
  merge = false,
): [ScopedState, boolean] => {
  let hasUpdated = false;
  const hasDeletion = Object.entries(scopedState).some(([_key, record]) => {
    return !footnotes.find((f) => record.content === f.content);
  });
  const newScopedState = footnotes.reduce((result, current) => {
    const stateRecord = scopedState[current.content];

    hasUpdated = hasUpdated || (!!stateRecord && current.index !== stateRecord.index);

    // Set value if footnote record key is defined previously
    // or force to add the record in the new state
    if (stateRecord || merge) {
      result[current.content] = current;
    }

    return result;
  }, {} as ScopedState);

  return [merge ? { ...scopedState, ...newScopedState } : newScopedState, hasUpdated || hasDeletion];
};

const mergeFootnoteGroups = (baseGroups: FootnoteGroup[], targetGroups: FootnoteGroup[]) => {
  const newGroups: FootnoteGroup[] = targetGroups.filter(
    (targetGroup) => !baseGroups.length || baseGroups.some((baseGroup) => baseGroup.type !== targetGroup.type),
  );
  const newBaseGroup: FootnoteGroup[] = baseGroups.map((currentGroup) => {
    const targetGroup = targetGroups.find((group) => currentGroup.type === group.type);
    const newTargetItems =
      targetGroup?.items?.filter((targetGroupItem) => !currentGroup.items.includes(targetGroupItem)) || [];

    return {
      ...currentGroup,
      items: [...currentGroup.items, ...newTargetItems],
    };
  });

  return [...newBaseGroup, ...newGroups];
};

const footnoteScope = 'global';

export function useOWCPriceDisplayFootnotes(
  onFootnoteReferencesUpdate: (footnoteReferenceGroups: FootnoteReferenceGroup[]) => void,
  reference: string,
): {
  addGroup: (groups: FootnoteGroup[]) => Promise<void>;
  removeGroup: (groups: FootnoteGroup[]) => Promise<void>;
  cleanupFootnotes: () => Promise<void>;
  scrollToFootnote: (index: number) => void;
} {
  let scopedState: ScopedState = {};
  let footnoteGroups: FootnoteGroup[] = [];

  const triggerReferenceUpdate = () => {
    const groupsReferences = footnoteGroups
      .map((group) => {
        return {
          type: group.type,
          items: group.items
            .map((groupItem) => Number(scopedState[groupItem]?.index))
            .filter((groupItem) => !!groupItem),
        };
      })
      .filter((group) => group.items.length);

    // only initialize if there are footnote groups references
    if (groupsReferences.length) {
      onFootnoteReferencesUpdate(groupsReferences);
    }
  };

  const addGroup = async (groups: FootnoteGroup[]) => {
    const footnotesInput = prepareInput(groups, reference);

    // Create footnotes records and produce footnote result with the related footnote input text
    const footnotesResult: { key: string; result: GlobalFootnoteRecord }[] = [];
    const results = await Promise.allSettled(footnotesInput.map(({ payload }) => createFootnote(payload)));

    for (const [index, result] of results.entries()) {
      if (result.status === 'fulfilled' && result.value) {
        footnotesResult.push({ key: footnotesInput[index].key, result: result.value });
      }
    }

    // Update footnoteGroups in order to use correct footnote text based on
    // footnote manager response
    const newFootnoteGroups = groups.map((group) => {
      return {
        type: group.type,
        items: group.items.map((groupItem) => footnotesResult.find((f) => f.key === groupItem)?.result.content || ''),
      };
    });

    footnoteGroups = mergeFootnoteGroups(footnoteGroups, newFootnoteGroups);

    // Maintain a local state in order to keep up to date the footnotes indexes when change
    // has been emitted from store
    const [newState] = produceScopedState(
      scopedState,
      footnotesResult.map((footnote) => footnote.result),
      true,
    );

    scopedState = newState;
    triggerReferenceUpdate();
  };

  const [subscription] = addFootnoteByScopeSubscription(footnoteScope, (foundFootnotes) => {
    // Update scopedState if current footnotes references indexes have changed
    const [newScopedState, shouldUpdate] = produceScopedState(scopedState, foundFootnotes);

    scopedState = newScopedState;

    if (shouldUpdate) {
      triggerReferenceUpdate();
    }
  });

  const removeGroup = async (groups: FootnoteGroup[]) => {
    if (!groups?.length) {
      return;
    }

    const records = groups.flatMap((footnote) => footnote.items).map((item) => scopedState[item]);

    await Promise.allSettled(
      records.map(({ id }) => {
        return deleteFootnote({ footnoteId: id, reference });
      }),
    );

    footnoteGroups = footnoteGroups
      .map(({ type, items }) => {
        const footnotes = records.map((r) => r.content);

        return {
          type,
          items: items.filter((item) => !footnotes.includes(item)),
        };
      })
      .filter((group) => group.items.length);
  };

  const cleanupFootnotes = async (): Promise<void> => {
    subscription?.unsubscribe();
    await Promise.allSettled(
      getFootnoteIds(scopedState).map((footnoteId) => {
        return deleteFootnote({ footnoteId, reference });
      }),
    );
  };

  const scrollToFootnote = (index: number): void => {
    const footnoteRecord = Object.values(scopedState).find((state) => state.index === index.toString());
    const footnoteComponent =
      document.getElementById(FOOTNOTE_CONTAINER_ID) ||
      (document.getElementsByTagName('mbmxp-footnote-component')[0] as HTMLElement);

    if (footnoteRecord?.id) {
      const singleFootnoteElement = footnoteComponent.shadowRoot?.getElementById(footnoteRecord.id);

      if (singleFootnoteElement) {
        const offsetTop = singleFootnoteElement.offsetTop;

        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      }
    } else {
      const offsetTop = footnoteComponent?.offsetTop;

      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  return {
    addGroup,
    removeGroup,
    cleanupFootnotes,
    scrollToFootnote,
  };
}
